/**
 * Toggle Component
 *
 * Toggle switch with label and description.
 *
 * @package PresszoneComments
 */

import { el } from '../utils/dom.js';

/**
 * Render a toggle switch button (button[role="switch"]).
 * Useful for compact UIs like tables.
 */
export function ToggleSwitch({
    checked = false,
    label = '',
    onChange = null,
    disabled = false,
    isWarning = false,
    optimistic = true,
    className = '',
    attrs = {}
} = {}) {
    const initialActive = !!checked;

    const classes = [
        'presszone-multilingual-toggle',
        initialActive ? 'presszone-multilingual-toggle--active' : '',
        isWarning ? 'presszone-multilingual-toggle--warning' : '',
        className
    ].filter(Boolean);

    let toggle;
    toggle = el('button', {
        type: 'button',
        role: 'switch',
        'aria-checked': initialActive ? 'true' : 'false',
        'aria-label': label,
        'aria-disabled': disabled ? 'true' : undefined,
        disabled: disabled || undefined,
        class: classes.join(' '),
        onclick: () => {
            if (disabled) return;

            const isActive = toggle.getAttribute('aria-checked') === 'true';
            const newValue = !isActive;
            if (optimistic) {
                toggle.classList.toggle('presszone-multilingual-toggle--active', newValue);
                toggle.setAttribute('aria-checked', newValue ? 'true' : 'false');
            }
            if (typeof onChange === 'function') onChange(newValue);
        },
        onkeydown: (e) => {
            if (disabled) return;
            // Space and Enter toggle the switch
            if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                toggle.click();
            }
        },
        ...attrs
    });

    return toggle;
}

/**
 * Render a toggle switch row.
 *
 * @param {string} key - State key
 * @param {string} title - Toggle label
 * @param {string} description - Help text
 * @param {Object} state - State object containing value
 * @param {Function} onUpdate - Callback when value changes (key, value)
 * @param {boolean} isWarning - Use warning styling
 */
export function Toggle(key, title, description, state, onUpdate, isWarning = false, options = {}) {
    const disabled = !!options.disabled;
    const isActive = !!state[key];

    const toggle = ToggleSwitch({
        checked: isActive,
        label: title,
        disabled,
        isWarning,
        onChange: (newValue) => {
            state[key] = newValue;
            if (onUpdate) onUpdate(key, newValue);
        }
    });

    return el('div', { class: 'presszone-multilingual-toggle-row' },
        el('div', { class: 'presszone-multilingual-toggle__label' },
            el('span', { class: 'presszone-multilingual-label--settings' }, title),
            description ? el('span', { class: 'presszone-multilingual-toggle-row__desc' }, description) : null
        ),
        toggle
    );
}

export default Toggle;
